
import { useState, useEffect } from 'react';
import { trackAddToCart, trackViewCart } from '../utils/analytics';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  variant?: string;
  variantId?: string;
}

export const useCart = () => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          setItems(parsedCart);
        }
      }
    } catch (error) {
      console.log('Error loading cart:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('cart', JSON.stringify(items));
      } catch (error) {
        console.log('Error saving cart:', error);
      }
    }
  }, [items, isLoaded]);

  const addItem = (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    const quantity = item.quantity || 1;
    
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        cartItem => cartItem.id === item.id && cartItem.variantId === item.variantId
      );

      let newItems;
      if (existingItemIndex > -1) {
        newItems = [...prevItems];
        newItems[existingItemIndex].quantity += quantity;
      } else {
        newItems = [...prevItems, { ...item, quantity }];
      }

      // Track add to cart event
      trackAddToCart({
        item_id: item.variantId || item.id,
        item_name: item.variant ? `${item.name} - ${item.variant}` : item.name,
        item_category: 'Décoration Noël',
        price: item.price,
        quantity: quantity,
        currency: 'EUR'
      });

      return newItems;
    });
  };

  const removeItem = (id: string, variantId?: string) => {
    setItems(prevItems => 
      prevItems.filter(item => !(item.id === id && item.variantId === variantId))
    );
  };

  const updateQuantity = (id: string, quantity: number, variantId?: string) => {
    if (quantity <= 0) {
      removeItem(id, variantId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.id === id && item.variantId === variantId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getItemCount = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const viewCart = () => {
    // Track view cart event
    trackViewCart({
      currency: 'EUR',
      value: getTotal(),
      items: items.map(item => ({
        item_id: item.variantId || item.id,
        item_name: item.variant ? `${item.name} - ${item.variant}` : item.name,
        item_category: 'Décoration Noël',
        price: item.price,
        quantity: item.quantity
      }))
    });
  };

  // Retourner un objet cart avec toutes les propriétés nécessaires
  const cart = {
    items,
    itemCount: getItemCount(),
    total: getTotal()
  };

  return {
    cart,
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemCount,
    getTotal,
    viewCart,
    isLoaded
  };
};
